// Whether a roster, as a move would leave it, may stand: the one check an
// add, a claim and the waiver run share. A player on IR counts toward no limit
// (D9), and a team holding one who has to come off adds nobody.

import { rosterProblem, starterShortfall } from '@fsp/domain';
import type { WaiverContext } from './types';

/** The roster less the players on IR (D9): what every limit counts. */
export function activeOf(teamId: string, roster: readonly string[], ctx: WaiverContext): string[] {
  const held = ctx.reserved?.(teamId);
  return held === undefined || held.size === 0 ? [...roster] : roster.filter((id) => !held.has(id));
}

/**
 * The roster as it would stand after an add: nobody on IR who has to come off
 * (D9), legal by count less IR, and laid out in the host's lineup (`fits`).
 * One copy, for the add, the claim and the run.
 */
export function fitProblem(
  teamId: string,
  after: readonly string[],
  ctx: WaiverContext,
): string | null {
  const active = activeOf(teamId, after, ctx);
  return (
    ctx.reserveHold?.(teamId, after) ??
    rosterProblem(active, ctx.roster, ctx.positionOf) ??
    ctx.fits?.(teamId, active) ??
    null
  );
}

/**
 * Why the roster a drop leaves may not stand: only a starting lineup it can
 * no longer fill, where the league forbids an empty slot. A drop never adds to
 * a roster over a limit, so it is never refused for one — a roster two over
 * its size could drop nobody, every drop answering "No room" (the review of
 * D9).
 */
export function dropLeavesProblem(
  teamId: string,
  after: readonly string[],
  ctx: WaiverContext,
): string | null {
  if (ctx.roster.allowEmptySlots) return null;
  const short = starterShortfall(activeOf(teamId, after, ctx), ctx.roster, ctx.positionOf);
  return short ? `Short a ${short} for the starting lineup.` : null;
}
